import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiBarChart2, FiPieChart, FiCalendar, FiDownload, FiUsers, FiClock, FiStar, FiActivity, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { fetchTickets, fetchUsers } from '../api/api';
import './Analytics.css';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [tickets, setTickets] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (timeRange === '7days') params.period = 'weekly';
    else if (timeRange === '30days') params.period = 'monthly';
    else if (timeRange === '90days') params.period = 'yearly';
    
    Promise.all([
      fetchTickets(params),
      fetchUsers('engineer')
    ]).then(([ticketsData, engineersData]) => {
      if (!cancelled) {
        setTickets(Array.isArray(ticketsData) ? ticketsData : []);
        setEngineers(Array.isArray(engineersData) ? engineersData : []);
      }
    }).catch(() => {
      if (!cancelled) {
        setTickets([]);
        setEngineers([]);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    
    return () => { cancelled = true; };
  }, [timeRange]);

  const getCreatedAt = (t) => new Date(t.created_at || t.createdAt || 0).getTime();
  const now = Date.now();
  const days = timeRange === '7days' ? 7 : timeRange === '90days' ? 90 : timeRange === '1year' ? 365 : 30;
  const cutoff = now - days * 24 * 60 * 60 * 1000;
  const filtered = tickets.filter((t) => getCreatedAt(t) >= cutoff);

  const totalTickets = filtered.length;
  const resolvedTickets = filtered.filter((t) => t.status === 'resolved' || t.status === 'closed').length;
  const resolutionRate = totalTickets ? ((resolvedTickets / totalTickets) * 100).toFixed(1) : 0;

  const getResolutionTime = (t) => {
    const resolved = t.resolved_at || t.resolvedAt;
    const created = t.created_at || t.createdAt;
    if (!resolved || !created) return null;
    return (new Date(resolved).getTime() - new Date(created).getTime()) / (1000 * 60 * 60);
  };
  const resolutionTimes = filtered.map(getResolutionTime).filter((r) => r != null);
  const avgResolutionTime = resolutionTimes.length
    ? (resolutionTimes.reduce((a, b) => a + b, 0) / resolutionTimes.length).toFixed(1)
    : '—';

  const ratings = filtered.map((t) => t.rating).filter((r) => typeof r === 'number');
  const avgSatisfaction = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(2) : '—';

  // Enhanced insights
  const openTickets = filtered.filter((t) => t.status === 'open').length;
  const inProgressTickets = filtered.filter((t) => t.status === 'in-progress').length;
  
  // Engineer performance
  const engineerPerformance = engineers.map(engineer => {
    const engineerTickets = filtered.filter(t => t.assigned_to === engineer.id || t.assignedTo === engineer.id);
    const resolved = engineerTickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;
    const times = engineerTickets.map(getResolutionTime).filter(r => r != null);
    const avgTime = times.length ? (times.reduce((a, b) => a + b, 0) / times.length).toFixed(1) : '—';
    
    return {
      name: engineer.name,
      email: engineer.email,
      total: engineerTickets.length,
      resolved,
      rate: engineerTickets.length ? ((resolved / engineerTickets.length) * 100).toFixed(1) : 0,
      avgTime
    };
  }).filter(e => e.total > 0).sort((a, b) => b.resolved - a.resolved);

  // Daily trends
  const dailyTrends = {};
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    dailyTrends[date] = { created: 0, resolved: 0 };
  }
  
  filtered.forEach(ticket => {
    const createdDate = new Date(getCreatedAt(ticket)).toISOString().split('T')[0];
    if (dailyTrends[createdDate]) dailyTrends[createdDate].created++;
    
    if (ticket.status === 'resolved' || ticket.status === 'closed') {
      const resolvedDate = new Date(ticket.resolved_at || ticket.resolvedAt).toISOString().split('T')[0];
      if (dailyTrends[resolvedDate]) dailyTrends[resolvedDate].resolved++;
    }
  });

  const trendData = Object.entries(dailyTrends).map(([date, data]) => ({ date, ...data }));

  const byStatus = [
    { status: 'Open', count: filtered.filter((t) => t.status === 'open').length },
    { status: 'In Progress', count: filtered.filter((t) => t.status === 'in-progress').length },
    { status: 'Resolved', count: filtered.filter((t) => t.status === 'resolved').length },
    { status: 'Closed', count: filtered.filter((t) => t.status === 'closed').length },
  ].filter((s) => s.count > 0);


  const byCategory = {};
  filtered.forEach((t) => {
    const cat = t.category || 'Other';
    byCategory[cat] = (byCategory[cat] || 0) + 1;
  });
  const categoryList = Object.entries(byCategory).map(([category, count]) => ({ category, count }));

  const totalStatus = byStatus.reduce((s, i) => s + i.count, 0);
  const totalCategory = categoryList.reduce((s, i) => s + i.count, 0);

  return (
    <div className="analytics-page">
      <div className="page-header">
        <div>
          <h1>Analytics & Reports</h1>
          <p>Insights from live ticket data</p>
        </div>
        <div className="header-actions">
          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="time-range-select">
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="1year">Last Year</option>
          </select>
          <button 
            className="export-btn"
            onClick={() => {
              const reportData = {
                timeRange,
                generatedAt: new Date().toISOString(),
                overview: {
                  totalTickets,
                  resolvedTickets,
                  resolutionRate,
                  avgResolutionTime,
                  avgSatisfaction,
                  openTickets,
                  inProgressTickets,
                  activeEngineers: engineerPerformance.length
                },
                byStatus,
                byCategory: categoryList,
                engineerPerformance,
                dailyTrends: trendData
              };
              
              const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `analytics-report-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
          >
            <FiDownload /> Export Report
          </button>
        </div>
      </div>

      {loading ? (
        <div className="analytics-loading">
          <div className="loading-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      ) : (
        <>
          <div className="overview-cards">
            <div className="overview-card">
              <div className="card-header"><h3>Total Tickets</h3><FiBarChart2 className="card-icon" /></div>
              <div className="card-value">{totalTickets}</div>
              <div className="card-insight">
                <FiActivity /> {openTickets} pending
              </div>
            </div>
            <div className="overview-card">
              <div className="card-header"><h3>Resolution Rate</h3><FiPieChart className="card-icon" /></div>
              <div className="card-value">{resolutionRate}%</div>
              <div className="card-insight">
                {resolutionRate >= 80 ? <FiTrendingUp /> : <FiTrendingDown />}
                {resolutionRate >= 80 ? 'Good' : 'Needs Improvement'}
              </div>
            </div>
            <div className="overview-card">
              <div className="card-header"><h3>Avg Resolution Time</h3><FiCalendar className="card-icon" /></div>
              <div className="card-value">{avgResolutionTime} h</div>
              <div className="card-insight">
                <FiClock /> {inProgressTickets} in progress
              </div>
            </div>
            <div className="overview-card">
              <div className="card-header"><h3>Customer Satisfaction</h3><FiTrendingUp className="card-icon" /></div>
              <div className="card-value">{avgSatisfaction}/5.0</div>
              <div className="card-insight">
                <FiStar /> {ratings.length} ratings
              </div>
            </div>
              <div className="overview-card">
              <div className="card-header"><h3>Active Engineers</h3><FiUsers className="card-icon" /></div>
              <div className="card-value">{engineerPerformance.length}</div>
              <div className="card-insight">
                <FiUsers /> {engineers.length} total
              </div>
            </div>
          </div>

          <div className="charts-section">
            <div className="chart-card">
              <div className="chart-header"><h2>Tickets by Status</h2></div>
              <div className="pie-chart-container">
                <div className="pie-chart">
                  {byStatus.length && totalStatus ? (
                    <div
                      className="pie-chart-single"
                      style={{
                        background: `conic-gradient(${byStatus.map((s, i) => {
                          const pct = (s.count / totalStatus) * 100;
                          const start = byStatus.slice(0, i).reduce((sum, x) => sum + (x.count / totalStatus) * 100, 0) * 3.6;
                          const end = start + pct * 3.6;
                          const colors = ['var(--primary)', 'var(--warning)', 'var(--success)', '#9E9E9E'];
                          return `${colors[i % colors.length]} ${start}deg ${end}deg`;
                        }).join(', ')})`,
                      }}
                    />
                  ) : (
                    <div className="pie-chart-empty">No data</div>
                  )}
                </div>
                <div className="pie-legend">
                  {byStatus.map((s, i) => (
                  <div key={i} className="legend-row">
                    <div className="legend-info">
                      <span className="legend-dot" style={{
                        backgroundColor: ['#F57C00', '#FFC107', '#4CAF50', '#9E9E9E'][i % 4]
                      }}></span>
                      <span className="legend-label">{s.status}</span>
                    </div>
                    <div className="legend-values">
                      <span className="legend-count">{s.count}</span>
                      <span className="legend-percentage">{totalStatus ? ((s.count / totalStatus) * 100).toFixed(1) : 0}%</span>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="detail-card">
              <div className="detail-header"><h2>Engineer Performance</h2></div>
              <div className="engineer-performance-list">
                {engineerPerformance.map((engineer, i) => (
                  <div className="engineer-performance-item">
                    <div className="engineer-info">
                      <div className="engineer-name">{engineer.name}</div>
                      <div className="engineer-email">{engineer.email}</div>
                      <div className="engineer-phone">{engineer.phone || 'N/A'}</div>
                    </div>
                    <div className="engineer-stats">
                      <div className="stat-item">
                        <span className="stat-label">Tickets</span>
                        <span className="stat-value">{engineer.total}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Resolved</span>
                        <span className="stat-value">{engineer.resolved}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Rate</span>
                        <span className="stat-value rate-badge" style={{
                          backgroundColor: engineer.rate >= 80 ? 'var(--success)' : engineer.rate >= 50 ? 'var(--warning)' : 'var(--error)',
                          color: 'white'
                        }}>{engineer.rate}%</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Avg Time</span>
                        <span className="stat-value">{engineer.avgTime}h</span>
                      </div>
                    </div>
                  </div>
                ))}
                {engineerPerformance.length === 0 && <p>No engineer activity in this period.</p>}
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-header"><h2>Tickets by Category</h2></div>
              <div className="category-list">
                {categoryList.map((item, i) => {
                  const pct = totalCategory ? (item.count / totalCategory) * 100 : 0;
                  const categoryIcons = {
                    'lock_issue': '🔒',
                    'pallet_movement': '📦',
                    'electrical': '⚡',
                    'hydraulic_leakage': '💧',
                    'payment': '💳',
                    'other': '📋'
                  };
                  const icon = categoryIcons[item.category.toLowerCase()] || '📋';
                  
                  return (
                    <div key={i} className="category-item">
                      <div className="category-header">
                        <span className="category-label">
                          <span className="category-icon">{icon}</span>
                          {item.category.replace('_', ' ')}
                        </span>
                        <span className="category-count">{item.count}</span>
                      </div>
                      <div className="category-bar">
                        <div className="category-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="category-stats">
                        <span className="category-percentage">{pct.toFixed(1)}%</span>
                        <span className="category-trend">
                          {pct > 30 ? '🔥 High activity' : pct > 15 ? '📊 Moderate' : '📉 Low activity'}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {categoryList.length === 0 && <p className="no-data">No tickets in this period.</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
